import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { eur } from "@/lib/format";
import { useT } from "@/i18n";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, Plus, Pencil, Copy, Sparkles } from "lucide-react";

export default function MeinKreis() {
  const t = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const BILLING_LABEL = { one_time: t("mk.bl_one_time"), monthly: t("mk.bl_monthly"), yearly: t("mk.bl_yearly") };

  useEffect(() => {
    if (!user || user.role !== "coach") return;
    api.get("/circles")
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : data.rows || [];
        setCircles(rows.filter((c) => c.circle_kind === "individual"));
      })
      .catch((err) => toast.error(apiError(err, t("cir.load_error"))))
      .finally(() => setLoading(false));
  }, [user]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const copyCode = (code) => { navigator.clipboard?.writeText(code); toast.success(t("cir.code_copied")); };

  return (
    <div>
      <Seo title={t("mk.seo")} noindex />
      <Link to="/app/coach/circles" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="mein-kreis-back">
        <ChevronLeft className="h-4 w-4" /> {t("cir.title")}
      </Link>
      <PageHeader
        eyebrow={t("mk.eyebrow")}
        title={t("mk.title")}
        subtitle={t("mk.subtitle")}
        action={
          <Button className="gap-1.5 rounded-full" onClick={() => navigate("/app/coach/circles/mein-kreis/edit")} data-testid="mein-kreis-new-cta">
            <Plus className="h-4 w-4" /> {t("mk.new")}
          </Button>
        }
      />

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
      ) : circles.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
            <Sparkles className="h-6 w-6" />
          </div>
          <p className="mt-4 font-display text-lg text-foreground">{t("mk.empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("mk.empty_desc")}</p>
          <Button className="mt-5 gap-1.5 rounded-full" onClick={() => navigate("/app/coach/circles/mein-kreis/edit")} data-testid="mein-kreis-empty-cta">
            <Plus className="h-4 w-4" /> {t("mk.create_circle")}
          </Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {circles.map((c) => (
            <div key={c.id} data-testid={`mein-kreis-item-${c.id}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 font-semibold text-foreground">{c.name}</p>
                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                  {c.price_cents
                    ? `${eur(c.price_cents)} ${BILLING_LABEL[c.billing_period] || ""}`
                    : c.price_credits
                      ? `${c.price_credits} Credits ${BILLING_LABEL[c.billing_period] || ""}`
                      : t("mk.free")}
                </span>
              </div>
              {c.description && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{c.description}</p>}
              <p className="mt-2 text-xs text-muted-foreground">
                {c.seat_limit ? t("mk.seats", { n: c.seat_limit }) : t("mk.unlimited_seats")}
              </p>
              <div className="mt-3 flex items-center gap-2">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => navigate(`/app/coach/circles/mein-kreis/edit?circleId=${c.id}`)} data-testid={`mein-kreis-edit-${c.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> {t("mk.edit")}
                </Button>
                <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => copyCode(c.invitation_code)} data-testid={`mein-kreis-copy-${c.id}`}>
                  <Copy className="h-3.5 w-3.5" /> {c.invitation_code}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
